import jwtDecode from 'jwt-decode'
import { CognitoDeviceAuthService } from '../services/cognito_device_auth.service'

const STORAGE_PREFIX = 'gooseDeviceAuth'

const KEYS = {
  idToken: `${STORAGE_PREFIX}.idToken`,
  accessToken: `${STORAGE_PREFIX}.accessToken`,
  refreshToken: `${STORAGE_PREFIX}.refreshToken`,
  locationName: `${STORAGE_PREFIX}.locationName`,
  deviceId: `${STORAGE_PREFIX}.deviceId`,
} as const

export interface DeviceAuthTokens {
  idToken: string
  accessToken: string
  refreshToken: string
  locationName: string
  deviceId: string
}

type JwtPayload = {
  exp?: number
} & Record<string, unknown>

const deviceClaimRegex = /^DEVICE:([^:]+):(.+)$/
const locationClaimRegex = /^L:([^:]+):TERMINAL$/

export const getLocationNameFromIdToken = (idToken: string): string | null => {
  try {
    const payload = jwtDecode<JwtPayload>(idToken)
    const groups = (payload['cognito:groups'] as string[]) || []

    const deviceMatch = groups.find((g) => deviceClaimRegex.test(g))
    if (deviceMatch) return deviceClaimRegex.exec(deviceMatch)?.[1] ?? null

    const locationMatch = groups.find((g) => locationClaimRegex.test(g))
    if (locationMatch) return locationClaimRegex.exec(locationMatch)?.[1] ?? null

    return null
  } catch {
    return null
  }
}

/** Check whether a JWT token's `exp` claim is still valid */
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = jwtDecode<JwtPayload>(token)
    if (!payload || typeof payload.exp !== 'number') return true
    return Date.now() >= (payload.exp - 60) * 1000
  } catch {
    return true
  }
}

export const setDeviceAuthTokens = (tokens: DeviceAuthTokens): void => {
  localStorage.setItem(KEYS.idToken, tokens.idToken)
  localStorage.setItem(KEYS.accessToken, tokens.accessToken)
  localStorage.setItem(KEYS.refreshToken, tokens.refreshToken)
  localStorage.setItem(KEYS.locationName, tokens.locationName)
  localStorage.setItem(KEYS.deviceId, tokens.deviceId)
}

export const getDeviceAuthTokens = (): DeviceAuthTokens | null => {
  const idToken = localStorage.getItem(KEYS.idToken)
  const accessToken = localStorage.getItem(KEYS.accessToken)
  const refreshToken = localStorage.getItem(KEYS.refreshToken)
  const locationName = localStorage.getItem(KEYS.locationName)
  const deviceId = localStorage.getItem(KEYS.deviceId)
  if (!idToken || !accessToken || !refreshToken || !locationName || !deviceId) return null
  return { idToken, accessToken, refreshToken, locationName, deviceId }
}

/**
 * Checks whether the device has stored auth tokens indicating a valid session.
 * Cognito refresh tokens are opaque (not JWTs), so we can't inspect their expiry
 * client-side. We only check that tokens exist; actual refresh token validity is
 * determined server-side when `getDeviceAccessToken` attempts a refresh.
 */
export const isDeviceAuthenticated = (): boolean => {
  return getDeviceAuthTokens() !== null
}

export const clearDeviceAuthTokens = (): void => {
  localStorage.removeItem(KEYS.idToken)
  localStorage.removeItem(KEYS.accessToken)
  localStorage.removeItem(KEYS.refreshToken)
  localStorage.removeItem(KEYS.locationName)
  localStorage.removeItem(KEYS.deviceId)
}

export const getDeviceId = (): string | null => {
  return localStorage.getItem(KEYS.deviceId)
}

export const getDeviceLocationName = (): string | null => {
  return localStorage.getItem(KEYS.locationName)
}

let refreshPromise: Promise<DeviceAuthTokens | null> | null = null

const refreshDeviceTokens = (currentTokens: DeviceAuthTokens): Promise<DeviceAuthTokens | null> => {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    try {
      const result = await CognitoDeviceAuthService.refreshSession(currentTokens.refreshToken)
      const { IdToken, AccessToken } = result.AuthenticationResult
      const refreshed: DeviceAuthTokens = {
        ...currentTokens,
        idToken: IdToken,
        accessToken: AccessToken,
      }
      setDeviceAuthTokens(refreshed)
      return refreshed
    } catch {
      clearDeviceAuthTokens()
      return null
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

/**
 * Returns a valid access token, refreshing via the refresh token if needed.
 * Deduplicates concurrent refresh calls so only one Cognito request is made.
 */
export const getDeviceAccessToken = async (): Promise<string | null> => {
  const tokens = getDeviceAuthTokens()
  if (!tokens) return null

  if (!isTokenExpired(tokens.accessToken)) {
    return tokens.accessToken
  }

  const refreshed = await refreshDeviceTokens(tokens)
  return refreshed?.accessToken ?? null
}

/**
 * Returns a valid id token, refreshing via the refresh token if needed.
 * Shares the in-flight refresh promise with getDeviceAccessToken so a single
 * Cognito request services both helpers.
 *
 * AppSync user-pool subscriptions authenticate with the id token (not the
 * access token), so realtime consumers must source their JWT from here.
 */
export const getDeviceIdToken = async (): Promise<string | null> => {
  const tokens = getDeviceAuthTokens()
  if (!tokens) return null

  if (!isTokenExpired(tokens.idToken)) {
    return tokens.idToken
  }

  const refreshed = await refreshDeviceTokens(tokens)
  return refreshed?.idToken ?? null
}
